import '.b6{flex-grow:0;}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useAuthState, useClient } from '@based/sdk/react'
import { Text } from '@app/components/core/typography/Text'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { Select } from '@app/components/core/input/select/Select'
import { styled } from 'inlines'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'

export const NotificationsEditPanel = ({
  profile,
}: {
  profile: PersonalProfileModel
}) => {
  const t = useTranslations().app
  const authState = useAuthState()
  const translations = useTranslations().app.dashboard.preferences
  const [updateEmailsEnabled, setUpdateEmailsEnabled] = useState(
    profile.updateEmailsEnabled ?? false,
  )
  const [
    importantNotificationsEmailsEnabled,
    setImportantNotificationsEmailsEnabled,
  ] = useState(profile.importantNotificationsEmailsEnabled ?? false)
  const [monthlyNewsletterEmailsEnabled, setMonthlyNewsletterEmailsEnabled] =
    useState(profile.monthlyNewsletterEmailsEnabled ?? false)
  const [
    crowdBuildingIntroductionEmailsEnabled,
    setCrowdBuildingIntroductionEmailsEnabled,
  ] = useState(profile.crowdBuildingIntroductionEmailsEnabled ?? false)

  const client = useClient()

  const selectOptions = useMemo(
    () => [
      {
        value: 'true',
        label: t.common.yes,
      },
      {
        value: 'false',
        label: t.common.no,
      },
    ],
    [t],
  )

  const save = useCallback(
    (value: ProfileEdit) => {
      void client.call('edit-profile', { id: authState.userId, value })
    },
    [authState.userId, client],
  )

  return (
    <Column
      alignItems="stretch"
      gap={'1.5rem'}
      style={{
        backgroundColor: 'var(--shades-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
        flex: '1 1 30rem',
      }}
    >
      <Title.H4 style={{ marginBottom: 0 }}>
        {translations.notificationSettings}
      </Title.H4>
      <NotificationEdit
        title={translations.updateEmailsTitle}
        subTitle={translations.updateEmailsSubtitle}
      >
        <Select
          variant="muted-blue"
          value={updateEmailsEnabled ? 'true' : 'false'}
          setValue={(value) => {
            const enabled = value === 'true'
            setUpdateEmailsEnabled(enabled)
            save({ updateEmailsEnabled: enabled })
          }}
          options={selectOptions}
        />
      </NotificationEdit>
      <NotificationEdit
        title={translations.importantNotificationsTitle}
        subTitle={translations.importantNotificationsSubtitle}
      >
        <Select
          variant="muted-blue"
          value={importantNotificationsEmailsEnabled ? 'true' : 'false'}
          setValue={(value) => {
            const enabled = value === 'true'
            setImportantNotificationsEmailsEnabled(enabled)
            save({ importantNotificationsEmailsEnabled: enabled })
          }}
          options={selectOptions}
        />
      </NotificationEdit>
      <NotificationEdit
        title={translations.monthlyNewsletterTitle}
        subTitle={translations.monthlyNewsletterSubtitle}
      >
        <Select
          variant="muted-blue"
          value={monthlyNewsletterEmailsEnabled ? 'true' : 'false'}
          setValue={(value) => {
            const enabled = value === 'true'
            setMonthlyNewsletterEmailsEnabled(enabled)
            save({ monthlyNewsletterEmailsEnabled: enabled })
          }}
          options={selectOptions}
        />
      </NotificationEdit>
      <NotificationEdit
        title={translations.crowdBuildingIntroductionTitle}
        subTitle={translations.crowdBuildingIntroductionSubtitle}
      >
        <Select
          variant="muted-blue"
          value={crowdBuildingIntroductionEmailsEnabled ? 'true' : 'false'}
          setValue={(value) => {
            const enabled = value === 'true'
            setCrowdBuildingIntroductionEmailsEnabled(enabled)
            save({ crowdBuildingIntroductionEmailsEnabled: enabled })
          }}
          options={selectOptions}
        />
      </NotificationEdit>
    </Column>
  )
}

type NotificationEditProps = {
  title: string
  subTitle: string
  children: ReactNode
}

const NotificationEdit = ({
  title,
  subTitle,
  children,
}: NotificationEditProps) => {
  return (
    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Column alignItems="stretch" gap={0}>
        <Text.Medium isBold={true}>{title}</Text.Medium>
        <Text.Small>{subTitle}</Text.Small>
      </Column>
      <div className="b6">{children}</div>
    </Row>
  )
}
